import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { householdsApi } from '@/lib/api';

interface Household {
  id: string;
  nombre: string;
  createdAt: string;
  updatedAt: string;
  miembrosCount: number;
  categoriasCount: number;
  presupuestosCount: number;
  movimientosCount: number;
}

interface HouseholdDetail {
  id: string;
  nombre: string;
  createdAt: string;
  updatedAt: string;
  miembros: Array<{
    id: string;
    email: string;
    nombre: string;
    rol: string;
    activo: boolean;
    createdAt: string;
  }>;
  stats: {
    categorias: number;
    presupuestos: number;
    movimientos: number;
    tarjetas: number;
    cuentas: number;
  };
}

interface HouseholdStats {
  periodo: string;
  ingresos: {
    total: number;
    count: number;
  };
  egresos: {
    total: number;
    count: number;
  };
  balance: number;
}

const rolColors: Record<string, string> = {
  OWNER: 'bg-purple-100 text-purple-800',
  ADMIN: 'bg-blue-100 text-blue-800',
  MEMBER: 'bg-gray-100 text-gray-800',
};

export default function Households() {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Fetch households list
  const { data, isLoading, error } = useQuery({
    queryKey: ['households', search],
    queryFn: () => householdsApi.getAll({ search: search || undefined, limit: 100 }),
  });

  // Fetch selected household details
  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['household', selectedId],
    queryFn: () => householdsApi.getById(selectedId!),
    enabled: !!selectedId,
  });

  // Fetch household stats
  const { data: statsData } = useQuery({
    queryKey: ['householdStats', selectedId],
    queryFn: () => householdsApi.getStats(selectedId!),
    enabled: !!selectedId,
  });

  const household: HouseholdDetail | undefined = detailData?.data?.household;
  const stats: HouseholdStats | undefined = statsData?.data?.stats;

  return (
    <div className="flex gap-6">
      {/* Household List */}
      <div className={`${selectedId ? 'w-1/3' : 'w-full'}`}>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Hogares</h1>
          <p className="text-gray-600">Gestion de hogares</p>
        </div>

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Buscar por nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input w-full"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            Error al cargar hogares: {(error as Error).message}
          </div>
        )}

        {/* Household List */}
        <div className="card">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {data?.data?.households?.map((h: Household) => (
                <div
                  key={h.id}
                  onClick={() => setSelectedId(h.id)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 ${
                    selectedId === h.id ? 'bg-primary-50 border-l-4 border-primary-500' : ''
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900">{h.nombre}</div>
                      <div className="text-sm text-gray-500">
                        Creado: {new Date(h.createdAt).toLocaleDateString('es-MX')}
                      </div>
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <div>{h.miembrosCount} miembros</div>
                      <div>{h.movimientosCount} movimientos</div>
                    </div>
                  </div>
                </div>
              ))}
              {data?.data?.households?.length === 0 && (
                <div className="p-8 text-center text-gray-500">No se encontraron hogares</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Household Detail */}
      {selectedId && (
        <div className="w-2/3">
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Detalle de Hogar</h2>
            <button
              onClick={() => setSelectedId(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              Cerrar
            </button>
          </div>

          {detailLoading ? (
            <div className="card p-8 text-center text-gray-500">Cargando...</div>
          ) : household ? (
            <div className="space-y-6">
              {/* Household Info Card */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{household.nombre}</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Creado:</span>{' '}
                    <span className="text-gray-900">
                      {new Date(household.createdAt).toLocaleDateString('es-MX')}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Actualizado:</span>{' '}
                    <span className="text-gray-900">
                      {new Date(household.updatedAt).toLocaleDateString('es-MX')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-5 gap-4">
                <div className="card text-center">
                  <div className="text-2xl font-bold text-primary-600">{household.stats.categorias}</div>
                  <div className="text-sm text-gray-500">Categorias</div>
                </div>
                <div className="card text-center">
                  <div className="text-2xl font-bold text-primary-600">{household.stats.presupuestos}</div>
                  <div className="text-sm text-gray-500">Presupuestos</div>
                </div>
                <div className="card text-center">
                  <div className="text-2xl font-bold text-primary-600">{household.stats.movimientos}</div>
                  <div className="text-sm text-gray-500">Movimientos</div>
                </div>
                <div className="card text-center">
                  <div className="text-2xl font-bold text-primary-600">{household.stats.tarjetas}</div>
                  <div className="text-sm text-gray-500">Tarjetas</div>
                </div>
                <div className="card text-center">
                  <div className="text-2xl font-bold text-primary-600">{household.stats.cuentas}</div>
                  <div className="text-sm text-gray-500">Cuentas</div>
                </div>
              </div>

              {/* Financial Stats */}
              {stats && (
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Resumen Financiero ({stats.periodo})
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="text-sm text-green-600 font-medium">Ingresos</div>
                      <div className="text-2xl font-bold text-green-700">
                        ${stats.ingresos.total.toFixed(2)}
                      </div>
                      <div className="text-xs text-green-600">{stats.ingresos.count} transacciones</div>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg">
                      <div className="text-sm text-red-600 font-medium">Egresos</div>
                      <div className="text-2xl font-bold text-red-700">
                        ${stats.egresos.total.toFixed(2)}
                      </div>
                      <div className="text-xs text-red-600">{stats.egresos.count} transacciones</div>
                    </div>
                    <div className={`p-4 rounded-lg ${stats.balance >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                      <div className={`text-sm font-medium ${stats.balance >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                        Balance
                      </div>
                      <div className={`text-2xl font-bold ${stats.balance >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                        ${stats.balance.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Members */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Miembros ({household.miembros.length})
                </h3>
                {household.miembros.length === 0 ? (
                  <p className="text-gray-500">Sin miembros</p>
                ) : (
                  <div className="space-y-3">
                    {household.miembros.map((m) => (
                      <div key={m.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium text-gray-900">{m.nombre}</div>
                          <div className="text-sm text-gray-500">{m.email}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${rolColors[m.rol] || 'bg-gray-100 text-gray-800'}`}>
                            {m.rol}
                          </span>
                          {m.activo ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Activo
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Inactivo
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card p-8 text-center text-gray-500">Hogar no encontrado</div>
          )}
        </div>
      )}
    </div>
  );
}
